import React from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";

import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import { Icon } from "src/design-system/Icon";
import { SdkScriptPill } from "src/settings/sdks/components/SdkScriptPill";
import { SdkBundleRow } from "src/settings/sdks/types";

const columnHelper = createColumnHelper<SdkBundleRow>();

const columns = [
  columnHelper.accessor("id", {
    header: (info) => (
      <Header info={info}>
        <span className="flex items-center gap-0.5">
          ID
          <Icon icon={faCircleInfo} size="2xs" variant="default" />
        </span>
      </Header>
    ),
    cell: (info) => (
      <Cell className="!h-auto min-h-[48px] items-center" info={info}>
        <CopyTextIcon
          dataLoc={`copy-bundle-id-${info.getValue()}`}
          feedback="toast"
          value={info.getValue()}
        />
      </Cell>
    ),
    size: 57,
  }),
  columnHelper.accessor("name", {
    header: (info) => <Header info={info}>Bundle name</Header>,
    cell: (info) => (
      <Cell className="!h-auto min-h-[48px] items-center" info={info}>
        {info.getValue()}
      </Cell>
    ),
    size: 324,
  }),
  columnHelper.accessor("scripts", {
    header: (info) => <Header info={info}>SDK Scripts</Header>,
    cell: (info) => (
      <Cell className="!h-auto min-h-[48px] items-center py-2" info={info}>
        <div className="flex flex-col justify-center gap-1.5">
          {info.getValue().map((script) => (
            <SdkScriptPill
              key={script.id}
              iconSrc={script.iconSrc}
              name={script.name}
            />
          ))}
        </div>
      </Cell>
    ),
    size: 324,
  }),
  columnHelper.accessor("lastUpdatedLabel", {
    header: (info) => <Header info={info}>Last updated</Header>,
    cell: (info) => (
      <Cell className="!h-auto min-h-[48px] items-center" info={info}>
        {info.getValue()}
      </Cell>
    ),
    size: 324,
  }),
  columnHelper.accessor("createdAtLabel", {
    header: (info) => <Header info={info}>Created at</Header>,
    cell: (info) => (
      <Cell className="!h-auto min-h-[48px] items-center" info={info}>
        {info.getValue()}
      </Cell>
    ),
    size: 324,
  }),
];

type SdkBundlesTableProps = {
  rows: SdkBundleRow[];
  isLoading?: boolean;
  onRowClick?: (row: SdkBundleRow) => void;
};

export const SdkBundlesTable: React.FC<SdkBundlesTableProps> = ({
  rows,
  isLoading = false,
  onRowClick,
}) => (
  <Table
    columns={columns}
    data={rows}
    dataLoc="sdk-bundles-table"
    disableVirtualization
    isLoading={isLoading}
    rowClassName={onRowClick ? "cursor-pointer" : undefined}
    rowPropsGetter={
      onRowClick
        ? (row) => ({ onClick: () => onRowClick(row.original) })
        : undefined
    }
    variant="compact"
  />
);
